/** @jsxImportSource @emotion/react */
import { useState, createContext, useContext, useEffect } from 'react'
import PropTypes from "prop-types";
import { Grid, Drawer, Card } from "antd";
import utils from 'utils'
import { MenuOutlined } from '@ant-design/icons';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { TEMPLATE, DARK_MODE, BORDER } from 'constants/ThemeConstant';
import { useSelector } from 'react-redux';

const { useBreakpoint } = Grid;

export const MobileHeaderContext = createContext(null);

export const useMobileHeader = (content) => {
	const setHeaderContent = useContext(MobileHeaderContext);
	useEffect(() => {
		if (setHeaderContent) {
			setHeaderContent(content);
		}
		return () => {
			if (setHeaderContent) {
				setHeaderContent(null);
			}
		};
	}, [content, setHeaderContent]);
};

const MainContent = styled.div(({ hasPageHeader, gutter }) => {

	const baseStyle = {
		minHeight: `calc(100vh - ${TEMPLATE.CONTENT_HEIGHT_OFFSET}px - ${TEMPLATE.LAYOUT_CONTENT_GUTTER}px * 2  - 2px);`,
		width: '100%',
		padding: gutter ? 24 : 0
	}

	if (hasPageHeader) {
		baseStyle.minHeight = '75vh'
	}

	return baseStyle
})


const SideContent = props => {
	const { sideContent, sideContentWidth = 250 } = props

	const currentTheme = useSelector(state => state.theme.currentTheme)

	return (
		<div style={{ width: `${sideContentWidth}px`, borderInlineEnd: `1px solid ${currentTheme === 'dark' ? DARK_MODE.BORDER_BASE_COLOR : BORDER.BASE_COLOR}` }}>
			{sideContent}
		</div>
	)
}

const SideContentMobile = props => {
	const { sideContent, visible, onSideContentClose, closable = true } = props

	const handleDrawerClick = (e) => {
		const target = e.target;
		const clickableMenuItem = target.closest('a, .ant-menu-item, .chat-menu-list-item, [role="menuitem"]');
		if (clickableMenuItem) {
			onSideContentClose();
		}
	};

	return (
		<Drawer
			width={320}
			placement="left"
			closable={closable}
			onClose={onSideContentClose}
			open={visible}
			bodyStyle={{ paddingLeft: 0, paddingRight: 0 }}
		>
			<div className="h-100" onClick={handleDrawerClick}>
				{sideContent}
			</div>
		</Drawer>
	)
}

export const InnerAppLayout = props => {
	const { mainContent, pageHeader, sideContentGutter = true, title } = props
	const isMobile = !utils.getBreakPoint(useBreakpoint()).includes('lg')
	const [visible, setVisible] = useState(false);
	const [headerContent, setHeaderContent] = useState(null);

	const close = (e) => {
		setVisible(false)
	}

	const openSideContentMobile = () => {
		setVisible(true)
	}

	return (
		<MobileHeaderContext.Provider value={setHeaderContent}>
			<Card className="mb-0" css={css`
				>.ant-card-body {
					display: flex;
					padding: 0px;
				}
			`}>
				{isMobile ?
					<SideContentMobile
						visible={visible}
						onSideContentClose={close}
						{...props}
					/>
					:
					<SideContent {...props} />
				}
				<MainContent hasPageHeader={pageHeader} gutter={sideContentGutter}>
					{isMobile ?
						<div 
							className={`font-size-lg ${!sideContentGutter ? 'pt-3 px-3' : ''}`}
							style={(headerContent || title) ? { 
								display: 'flex', 
								alignItems: 'center', 
								gap: '12px', 
								marginBottom: '20px',
								borderBottom: '1px solid #f0f0f0',
								paddingBottom: '12px'
							} : {}}
						>
							<MenuOutlined onClick={() => openSideContentMobile()} style={(headerContent || title) ? {} : { marginBottom: '16px' }} />
							{(headerContent || title) && (
								<div style={{ flex: 1, display: 'flex', alignItems: 'center' }}>
									{headerContent || (
										<span style={{ fontSize: '16px', fontWeight: '700', color: '#1a3353' }}>
											{title}
										</span>
									)}
								</div>
							)}
						</div>
						:
						null
					}
					{mainContent}
				</MainContent>
			</Card>
		</MobileHeaderContext.Provider>
	)
}

InnerAppLayout.propTypes = {
	sideContent: PropTypes.node,
	mainContent: PropTypes.node,
	pageHeader: PropTypes.bool,
	sideContentWidth: PropTypes.number,
	border: PropTypes.bool,
	sideContentGutter: PropTypes.bool,
	title: PropTypes.string
};

export default InnerAppLayout
